import { yupResolver } from "@hookform/resolvers/yup";
import {
  DialogContent,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Button,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "react-query";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { Messaggio } from "../prismaTypes";
import { changeDatesToUTC } from "../utils/dateUtils";
import * as yup from "yup";
import { Loading } from "../ui/Loading";
import { DatePicker } from "@mui/x-date-pickers";

const schema = yup.object({}).required();

export function MessaggioUi(props: { messaggio?: Messaggio; handleClose: () => void }) {
  const { messaggio, handleClose } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [animations, setAnimations] = useState(!messaggio);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let timeout = 0;
    if (loaded) timeout = window.setTimeout(() => setAnimations(true), 1);
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Messaggio>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const { mutate: mutateCreate } = useMutation(
    (data: Messaggio) => {
      return axios.post(API_URL + "messaggi", data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Comunicazione Creata", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Messaggio) => axios.patch(API_URL + "messaggi/" + messaggio?.id ?? -1, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Comunicazione Aggiornata", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const onSubmit = (data: Messaggio) => {
    changeDatesToUTC(data);
    if (messaggio) mutateUpdate(data);
    else mutateCreate(data);
  };

  useEffect(() => {
    if (!messaggio) {
      setValue("dataDal", null);
      setValue("dataFine", null);
      setValue("attivo", true);
      setValue("titolo", "");
      setValue("testo", "");
      setValue("displayOnSide", false);
      setValue("displayOnLogin", false);
    } else {
      setValue("id", messaggio.id);
      setValue("dataDal", messaggio.dataDal);
      setValue("dataFine", messaggio.dataFine);
      setValue("attivo", messaggio.attivo);
      setValue("titolo", messaggio.titolo);
      setValue("testo", messaggio.testo);
      setValue("displayOnSide", messaggio.displayOnSide);
      setValue("displayOnLogin", messaggio.displayOnLogin);
    }
    setLoaded(true);
  }, [messaggio, setValue]);

  if (!loaded) return <Loading />;
  if (!animations) return <></>;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={animations ? "" : "noAnimation"}>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Controller
              name="attivo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === true}
                      onChange={(v) => setValue("attivo", v.target.checked)}
                    />
                  }
                  label="Attiva"
                />
              )}
            />
            <Controller
              name="displayOnLogin"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === true}
                      onChange={(v) => setValue("displayOnLogin", v.target.checked)}
                    />
                  }
                  label="Al Login"
                />
              )}
            />
            <Controller
              name="displayOnSide"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === true}
                      onChange={(v) => setValue("displayOnSide", v.target.checked)}
                    />
                  }
                  label="Permanente"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="dataDal"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Dal"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      required
                      sx={{ mr: 2 }}
                      helperText={errors.dataDal?.message}
                      {...params}
                      error={Boolean(errors.dataDal)}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="dataFine"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Al"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      required
                      helperText={errors.dataFine?.message}
                      error={Boolean(errors.dataFine)}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="titolo"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="Titolo"
                  fullWidth
                  required
                  error={Boolean(errors.titolo)}
                  helperText={errors.titolo?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="testo"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="Testo"
                  fullWidth
                  required
                  multiline
                  rows={10}
                  error={Boolean(errors.testo)}
                  helperText={errors.testo?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Chiudi
        </Button>
        <Button type="submit" autoFocus>
          Salva
        </Button>
      </DialogActions>
    </form>
  );
}
